import { useState, useEffect, useContext } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, Building2, MapPin, Clock, Tag, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { API, AuthContext } from "../../App";

const CATEGORIES = [
  "Sports", "Wedding", "Portrait", "Event", "Commercial", 
  "Real Estate", "Product", "Fashion", "Newborn", "Family",
  "Landscape", "Concert", "Food", "Architecture", "Pet"
];

const TIMEZONES = [
  "America/New_York",
  "America/Chicago", 
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
  "Europe/London",
  "Europe/Paris",
  "Asia/Tokyo",
  "Australia/Sydney"
];

const OnboardingBusinessInfo = () => {
  const navigate = useNavigate();
  const { onboardingData, refreshData } = useOutletContext();
  const { refreshUser } = useContext(AuthContext);
  const [saving, setSaving] = useState(false);
  
  const [brandName, setBrandName] = useState("");
  const [customSlug, setCustomSlug] = useState("");
  const [useCustomSlug, setUseCustomSlug] = useState(false);
  const [categories, setCategories] = useState([]);
  const [location, setLocation] = useState("");
  const [timezone, setTimezone] = useState("America/New_York");

  useEffect(() => {
    if (onboardingData?.photographer) {
      const p = onboardingData.photographer;
      setBrandName(p.company_name || "");
      setCustomSlug(p.slug || "");
      setCategories(p.event_types || []);
      setLocation(p.location || "");
      setTimezone(p.timezone || "America/New_York");
    }
  }, [onboardingData]);

  const generateSlugPreview = () => {
    if (useCustomSlug && customSlug) {
      return customSlug.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-");
    }
    if (brandName) {
      return brandName.toLowerCase().replace(/[^a-z0-9\s]/g, "").replace(/\s+/g, "-");
    }
    return "your-business-name";
  };

  const toggleCategory = (category) => {
    setCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleContinue = async () => {
    if (!brandName.trim()) {
      toast.error("Please enter your brand/business name");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.put(`${API}/onboarding/step/business-info`, {
        brand_name: brandName.trim(),
        slug: useCustomSlug ? customSlug : null,
        categories: categories.length > 0 ? categories : null,
        location: location || null,
        timezone: timezone
      }, { headers });
      
      toast.success("Business info saved!");
      await refreshData();
      if (refreshUser) await refreshUser();
      navigate("/dashboard/onboarding/agreement");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="onboarding-step-1">
      {/* Welcome Message */}
      <div className="text-center mb-8">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4"
        >
          <Sparkles className="w-8 h-8 text-primary" />
        </motion.div>
        <h1 className="text-3xl font-serif mb-2">Welcome to 360Lumina!</h1>
        <p className="text-muted-foreground max-w-md mx-auto">
          Let&apos;s set up your photography business profile. This will only take a few minutes.
        </p>
      </div>

      {/* Form */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Business Information
          </CardTitle>
          <CardDescription>
            Tell us about your photography business
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Brand Name */}
          <div className="space-y-2">
            <Label htmlFor="brand-name">
              Brand / Business Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="brand-name"
              placeholder="e.g., Smith Photography, Capture Studio"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              data-testid="brand-name-input"
            />
            <p className="text-xs text-muted-foreground">
              This is how clients will see your business
            </p>
          </div>

          {/* Slug Preview */}
          <div className="p-4 bg-muted/30 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Your profile URL</Label>
              <button
                type="button"
                onClick={() => setUseCustomSlug(!useCustomSlug)}
                className="text-xs text-primary hover:underline"
              >
                {useCustomSlug ? "Use auto-generated" : "Customize URL"}
              </button>
            </div>
            
            {useCustomSlug ? (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">360lumina.com/photographer/</span>
                <Input
                  value={customSlug}
                  onChange={(e) => setCustomSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                  className="flex-1"
                  placeholder="your-custom-url"
                />
              </div>
            ) : (
              <p className="text-sm font-mono bg-background px-3 py-2 rounded border border-border">
                360lumina.com/photographer/<span className="text-primary">{generateSlugPreview()}</span>
              </p>
            )}
          </div>

          {/* Categories */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <Tag className="w-4 h-4" />
              Photography Categories
            </Label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map(category => (
                <Badge
                  key={category}
                  variant={categories.includes(category) ? "default" : "outline"}
                  className={`cursor-pointer transition-all ${
                    categories.includes(category) 
                      ? "bg-primary text-primary-foreground" 
                      : "hover:bg-muted"
                  }`}
                  onClick={() => toggleCategory(category)}
                >
                  {category}
                </Badge>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Select the types of photography you offer
            </p>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location" className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Location
            </Label>
            <Input
              id="location"
              placeholder="e.g., Los Angeles, CA"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>

          {/* Timezone */}
          <div className="space-y-2">
            <Label htmlFor="timezone" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Timezone
            </Label>
            <select
              id="timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            >
              {TIMEZONES.map(tz => (
                <option key={tz} value={tz}>{tz.replace("_", " ")}</option>
              ))}
            </select>
          </div>

          {/* Continue Button */}
          <div className="pt-4">
            <Button
              onClick={handleContinue}
              disabled={!brandName.trim() || saving}
              className="w-full"
              size="lg"
              data-testid="continue-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingBusinessInfo;
